import React, { useState, useEffect } from "react";
import Global from "./search/Global";
import Options from "./search/Options";
import Internal from "./search/Internal";
import InvestorContainer from "./search/InvestorContainer";
import axios from "axios";
import { BaseURL } from "../App";

// Recommendation Modal component
const RecommendationModal = ({ isOpen, onClose, onYes }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Modal content - positioned at bottom right */}
      <div className="absolute bottom-8 md:right-8 pointer-events-auto">
        <div className="bg-white p-6 rounded-lg shadow-2xl max-w-md animate-fade-in border border-gray-100">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Do you want me to recommend you something or help on this search?
            </h3>
            <div className="mt-6 flex justify-center gap-4">
              <button
                onClick={onYes}
                className="px-6 py-2 bg-[#fbbb04] text-white rounded-md hover:bg-[#e6ad04] transition-colors"
              >
                YES
              </button>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                NO
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable Pagination component
const Pagination = ({ currentPage, totalPages, btnArray, onPageChange }) => {
  if (totalPages <= 1) return null;

  return (
    <ol className="flex justify-center gap-1 text-xs font-medium my-8">
      {/* First page button */}
      {currentPage > 1 && (
        <li>
          <button
            onClick={() => onPageChange(currentPage - 1)}
            className="inline-flex size-8 items-center justify-center hover:bg-[#fbbb04] hover:text-white rounded-full border border-gray-100 bg-white text-gray-900"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </li>
      )}

      {/* Page numbers */}
      {btnArray.map((item, idx) => (
        <li key={idx}>
          {item === "..." ? (
            <div className="block size-8 rounded-full border-gray-100 border text-center leading-8 text-primary">
              {item}
            </div>
          ) : parseInt(item) === currentPage ? (
            <div className="block size-8 rounded-full border-[#fbbb04] bg-[#fbbb04] text-center leading-8 text-white">
              {item}
            </div>
          ) : (
            <button
              onClick={() => onPageChange(parseInt(item))}
              className="block size-8 hover:bg-[#fbbb04] hover:text-white rounded-full border border-gray-100 bg-white text-center leading-8 text-gray-900"
            >
              {item}
            </button>
          )}
        </li>
      ))}

      {/* Last page button */}
      {currentPage < totalPages && (
        <li>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            className="inline-flex size-8 items-center justify-center hover:bg-[#fbbb04] hover:text-white rounded-full border border-gray-100 bg-white text-gray-900"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </li>
      )}
    </ol>
  );
};

export default function Remote() {
  // State management
  const [searchParams, setSearchParams] = useState({
    search: "",
    type: "",
    category: "",
    page: 1,
    type_of: "",
  });
  const [standards, setStandards] = useState([]);
  const [fundingAgencies, setFundingAgencies] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    btnArray: [],
  });

  // Recommendation modal state
  const [isRecommendationModalOpen, setIsRecommendationModalOpen] =
    useState(false);

  // Get URL parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const newParams = { ...searchParams };

    if (queryParams.get("search")) newParams.search = queryParams.get("search");
    if (queryParams.get("type")) newParams.type = queryParams.get("type");
    if (queryParams.get("category"))
      newParams.category = queryParams.get("category");
    if (queryParams.get("page"))
      newParams.page = parseInt(queryParams.get("page")) || 1;
    if (queryParams.get("type_of"))
      newParams.type_of = queryParams.get("type_of");

    setSearchParams(newParams);

    setPagination((prev) => ({
      ...prev,
      currentPage: newParams.page,
    }));

    // If we have search parameters, fetch results
    if (Object.values(newParams).some((value) => value !== "" && value !== 1)) {
      fetchResults(newParams);
    }
  }, [window.location.search]);

  // Handle recommendation modal display timer
  useEffect(() => {
    let timerId;

    // Show recommendation modal 5 seconds after standards search results are displayed
    // Only show on the first page of standards results
    if (
      standards &&
      standards.length > 0 &&
      !loading &&
      searchParams.type === "standards" &&
      searchParams.page === 1
    ) {
      timerId = setTimeout(() => {
        setIsRecommendationModalOpen(true);
      }, 5000); // 5 seconds delay
    }

    // Clean up timer on unmount or when search params change
    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [standards, loading, searchParams.type, searchParams.page]);

  // Handle "YES" button click in recommendation modal
  const handleRecommendationYes = () => {
    setIsRecommendationModalOpen(false);
    // Redirect to the specified website
    // window.location.href = "https://www.gangesventure.com";
    // Redirect to the specified website in a new tab
    window.open(
      process.env.REACT_APP_PUBLIC_TESTING_NAVBAR_ROUTE_URL,
      "_blank"
    );
  };

  // Handle search form submission
  const handleSearchSubmit = (params) => {
    // Reset to page 1 when changing search parameters (unless explicitly setting page)
    const newParams = {
      ...searchParams,
      ...params,
      page: params.page || 1, // Reset to page 1 for new searches
    };
    setSearchParams(newParams);

    // Update URL with search parameters
    const url = new URL(window.location);
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        url.searchParams.set(key, value);
      } else {
        url.searchParams.delete(key);
      }
    });
    window.history.pushState({}, "", url);

    // Hide search results text area when changing search parameters
    if (params.type !== "global") {
      setShowSearchResults(false);
    }

    fetchResults(newParams);
  };

  // Handle page change for pagination
  const handlePageChange = (newPage) => {
    if (newPage !== pagination.currentPage) {
      const newParams = { ...searchParams, page: newPage };

      // Update URL
      const url = new URL(window.location);
      url.searchParams.set("page", newPage);
      window.history.pushState({}, "", url);

      // Update state
      setSearchParams(newParams);
      setPagination((prev) => ({
        ...prev,
        currentPage: newPage,
      }));

      // Fetch new results
      fetchResults(newParams);
    }
  };

  // Handle global search results
  const handleGlobalSearchResults = (results) => {
    setSearchResults(results);
    setShowSearchResults(true);
  };

  // Generate pagination buttons array
  const generatePaginationButtons = (current, total) => {
    let buttons = [];

    if (total <= 7) {
      // Show all pages if total is 7 or less
      for (let i = 1; i <= total; i++) {
        buttons.push(i);
      }
    } else {
      // Complex pagination with ellipsis
      if (current <= 3) {
        buttons = [1, 2, 3, 4, 5, "...", total];
      } else if (current >= total - 2) {
        buttons = [1, "...", total - 4, total - 3, total - 2, total - 1, total];
      } else {
        buttons = [1, "...", current - 1, current, current + 1, "...", total];
      }
    }

    return buttons;
  };

  // Fetch search results based on parameters
  const fetchResults = async (params) => {
    setLoading(true);

    try {
      let response;
      // Determine which API endpoint to use based on search type
      if (params.type === "standards") {
        response = await axios.get(BaseURL + "/api/search_standards/", {
          params,
        });
        setStandards(response.data.standards || []);
        setFundingAgencies([]);
        setInvestors([]);
        setPosts([]);

        // Update pagination
        const paginationData = response.data.pagination || {};
        setPagination({
          currentPage: paginationData.current_page || params.page,
          totalPages: paginationData.total_pages || 1,
          btnArray:
            paginationData.btnArray ||
            generatePaginationButtons(
              paginationData.current_page || params.page,
              paginationData.total_pages || 1
            ),
        });
      } else if (params.type === "funding_agencies") {
        // Use the same API endpoint but with the type parameter
        params.search = "Funding Agencies"; // Ensure the backend knows we're looking for funding agencies
        response = await axios.get(BaseURL + "/api/search_standards/", {
          params,
        });
        setStandards([]);
        setFundingAgencies(response.data.standards || []);
        setInvestors([]);
        setPosts([]);

        // Update pagination
        const paginationData = response.data.pagination || {};
        setPagination({
          currentPage: paginationData.current_page || params.page,
          totalPages: paginationData.total_pages || 1,
          btnArray:
            paginationData.btnArray ||
            generatePaginationButtons(
              paginationData.current_page || params.page,
              paginationData.total_pages || 1
            ),
        });
      } else if (
        params.type === "Investors" ||
        params.search?.toLowerCase().includes("investor")
      ) {
        response = await axios.get(BaseURL + "/api/search_investors/", {
          params,
        });
        setStandards([]);
        setFundingAgencies([]);
        setInvestors(response.data.investors || []);
        setPosts([]);

        // Update pagination
        const paginationData = response.data.pagination || {};
        setPagination({
          currentPage: paginationData.current_page || params.page,
          totalPages: paginationData.total_pages || 1,
          btnArray:
            paginationData.btnArray ||
            generatePaginationButtons(
              paginationData.current_page || params.page,
              paginationData.total_pages || 1
            ),
        });
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle service selection from Options component
  const handleServiceSelect = (service) => {
    handleSearchSubmit({ type: service, page: 1 });
  };

  return (
    <main className="max-w-[1280px] mx-auto px-5 sm:px-6 xl:px-0 w-full pt-28 md:pt-36 pb-10 mb-5 md:mb-14">
      <br />
      <div className="flex justify-center items-center min-h-screen ">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4 animate-pulse">
            Your link to remote control the equipments will appear here.
          </h1>
          <p className="text-gray-500">
            Please check your email for instructions
          </p>
        </div>
      </div>

      {/* Loading indicator */}
      {loading && (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#fbbb04]"></div>
        </div>
      )}
    </main>
  );
}
